import {
  type QbStatusResponse,
  QbStatusResponse as QbStatusResponseSchema,
} from '@uit/shared/schemas/quickbooks';
import { useState } from 'react';
import { apiMutation, useApi } from '../../lib/api.js';
import s from '../../pages/SettingsPage.module.scss';

// The Intuit OAuth callback redirects back to /settings?qb=<result>.
const QB_RESULT_MESSAGES: Record<string, string> = {
  connected: 'QuickBooks connected.',
  denied: 'QuickBooks connection was cancelled.',
  invalid: 'QuickBooks returned an invalid response — please try again.',
  state_mismatch: 'QuickBooks connection failed a security check — please try again.',
  exchange_failed: 'Could not complete the QuickBooks connection — please try again.',
};

function readQbResult(): string | null {
  const v = new URLSearchParams(window.location.search).get('qb');
  return v ? (QB_RESULT_MESSAGES[v] ?? null) : null;
}

export function QuickbooksSection() {
  const { data, refetch } = useApi<QbStatusResponse>(
    '/api/quickbooks/status',
    QbStatusResponseSchema,
  );
  const [result] = useState<string | null>(readQbResult);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OAuth is a full-page redirect chain, so navigate the browser (not fetch).
  const startConnect = (): void => {
    window.location.assign('/api/quickbooks/connect');
  };

  const onDisconnect = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiMutation('/api/quickbooks/disconnect', {}, { method: 'POST' });
      void refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const connected = data?.connected ?? false;
  const configured = data?.configured ?? false;

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>QuickBooks</h2>
      <p className={s.sectionSubtitle}>
        Connect the portal to QuickBooks Online so vendors, services, and bill lines can be pushed.
      </p>

      {result && (
        <div className={s.success} role="status">
          {result}
        </div>
      )}

      {!configured ? (
        <p className={s.sectionSubtitle}>
          QuickBooks credentials are not configured on the server yet.
        </p>
      ) : connected ? (
        <>
          <p className={s.sectionSubtitle}>
            Connected to the <strong>{data?.environment}</strong> company{' '}
            <strong>{data?.realm_id}</strong>. The access token renews automatically.
          </p>
          <div className={s.buttonRow}>
            <button type="button" className={s.button} onClick={startConnect}>
              Reconnect
            </button>
            <button
              type="button"
              className={s.buttonSecondary}
              disabled={busy}
              onClick={() => void onDisconnect()}
            >
              {busy ? 'Working…' : 'Disconnect'}
            </button>
          </div>
        </>
      ) : (
        <button type="button" className={s.button} onClick={startConnect}>
          Connect QuickBooks
        </button>
      )}

      {error && (
        <div className={s.error} role="alert">
          {error}
        </div>
      )}
    </section>
  );
}
